import useRollbarUtil from "@/components/hooks/useRollbarUtil";
import {
  Option,
  OptionStatus,
  Plan,
  PlanStatus,
  Service,
  useBankServicesQuery,
} from "@/generated/graphql_billing";
import { byDefaultSortOrder } from "@/libs/billing/plan";
import { filterActiveMikatanoServicesExceptLoan } from "@/libs/billing/service";
import config from "@/libs/config";
import { APIContext } from "@/libs/constants";
import { useMemo } from "react";

export const useBillingBankServices = () => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();

  const { loading, data, refetch } = useBankServicesQuery({
    variables: {
      bankcode: config.BANK_CODE,
    },
    onError: getNotifyErrorHandlerForGraphQL(
      "useBillingBankServices: error fetching bank services"
    ),
    context: {
      api: APIContext.BILLING,
    },
  });

  const bankServices = useMemo(() => data?.bankServices, [data]);

  const { activeBankServices, optionIDs } = useMemo<{
    activeBankServices: Service[];
    optionIDs: string[];
  }>(() => {
    if (!data || !data.bankServices) {
      return { activeBankServices: [], optionIDs: [] };
    }

    const activeBankServices = filterActiveMikatanoServicesExceptLoan(
      bankServices.services || []
    );

    const optionIDs = bankServices.options
      .filter((opt) => opt.status === OptionStatus.Active)
      .map((opt) => opt.id);

    return { activeBankServices, optionIDs };
  }, [data]);

  const plans: Plan[] =
    data?.bankServices?.plans?.toSorted(byDefaultSortOrder) || [];

  const activePlans: Plan[] =
    plans.filter((p) => p.status === PlanStatus.Active) || [];

  const options: Option[] =
    data?.bankServices?.options?.toSorted(byDefaultSortOrder) || [];

  const activeOptions: Option[] =
    options.filter((o) => o.status === OptionStatus.Active) || [];

  return {
    bankServices,
    activeBankServices,
    options,
    optionIDs,
    activeOptions,
    plans,
    activePlans,
    billingBankServicesLoading: loading,
    billingBankServicesRefetch: refetch,
  };
};
